import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import API_URL from '../../config/api';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&display=swap');

.trp-wrap {
  min-height: 100vh;
  background: linear-gradient(160deg, #064e3b 0%, #0d9488 40%, #0369a1 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  padding: 24px 16px;
  position: relative;
  overflow: hidden;
}
.trp-wrap::before {
  content: '';
  position: absolute;
  top: -20%; right: -10%;
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
  border-radius: 50%;
}
.trp-wrap::after {
  content: '';
  position: absolute;
  bottom: -15%; left: -8%;
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(3,105,161,0.25) 0%, transparent 70%);
  border-radius: 50%;
}
.trp-card {
  background: #fff;
  border-radius: 24px;
  padding: 44px 40px;
  max-width: 420px;
  width: 100%;
  box-shadow:
    0 20px 0 rgba(6,78,59,0.2),
    0 32px 64px rgba(0,0,0,0.22),
    inset 0 1px 0 rgba(255,255,255,0.9);
  position: relative;
  z-index: 1;
}
.trp-icon-wrap {
  width: 68px; height: 68px;
  background: linear-gradient(135deg, #064e3b, #0d9488, #0369a1);
  border-radius: 18px;
  display: flex; align-items: center; justify-content: center;
  font-size: 32px;
  margin: 0 auto 16px;
  box-shadow: 0 8px 24px rgba(13,148,136,0.35);
}
.trp-title {
  margin: 0 0 4px;
  font-size: 24px; font-weight: 800; color: #0f172a;
  text-align: center; letter-spacing: -0.02em;
}
.trp-subtitle {
  margin: 0 0 32px;
  font-size: 14px; color: #64748b;
  text-align: center; font-weight: 500; line-height: 1.5;
}
.trp-label {
  display: block; font-size: 13px; font-weight: 700;
  color: #374151; margin-bottom: 6px;
}
.trp-input {
  width: 100%; padding: 12px 14px;
  border: 1.5px solid #d1d5db; border-radius: 10px;
  font-size: 15px; outline: none;
  font-family: inherit; box-sizing: border-box;
  transition: border-color 0.15s;
  color: #0f172a;
}
.trp-input:focus { border-color: #0d9488; box-shadow: 0 0 0 3px rgba(13,148,136,0.1); }
.trp-strength {
  margin-top: 6px; height: 4px; border-radius: 2px;
  background: #e2e8f0; overflow: hidden;
}
.trp-strength-bar { height: 100%; border-radius: 2px; transition: width .3s, background .3s; }
.trp-btn {
  width: 100%;
  background: linear-gradient(135deg, #0d9488, #0891b2);
  color: #fff; border: none; border-radius: 11px;
  padding: 14px; font-size: 15px; font-weight: 800;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 0 rgba(6,78,59,0.3), 0 8px 20px rgba(13,148,136,0.35);
  transition: opacity 0.15s, transform 0.12s;
  letter-spacing: -0.01em;
}
.trp-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
.trp-btn:active:not(:disabled) { transform: translateY(1px); box-shadow: 0 2px 0 rgba(6,78,59,0.3); }
.trp-btn:disabled { background: #9ca3af; cursor: not-allowed; box-shadow: none; }
.trp-error {
  background: #fef2f2; border: 1.5px solid #fca5a5;
  border-radius: 9px; padding: 10px 14px;
  margin-bottom: 20px; font-size: 13px; color: #dc2626;
  display: flex; align-items: center; gap: 8px;
}
.trp-success {
  background: #f0fdf4; border: 1.5px solid #86efac;
  border-radius: 9px; padding: 16px 14px;
  font-size: 14px; color: #166534;
  text-align: center; line-height: 1.5;
}
.trp-invalid {
  text-align: center; padding: 12px 0;
  font-size: 14px; color: #dc2626; line-height: 1.6;
}
.trp-hint {
  text-align: center; margin-top: 22px;
  font-size: 13px; color: #64748b; line-height: 1.5;
}
.trp-hint a { color: #0d9488; font-weight: 700; text-decoration: none; }
.trp-hint a:hover { text-decoration: underline; }
.trp-footer {
  position: relative; z-index: 1;
  margin-top: 20px; font-size: 12px;
  color: rgba(255,255,255,0.7); text-align: center;
}
.trp-footer a { color: #a7f3d0; text-decoration: none; font-weight: 600; }
.trp-footer a:hover { color: #fff; }
`;

const getStrength = (pw: string): { width: string; color: string; label: string } => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  if (score <= 1) return { width: '20%', color: '#ef4444', label: 'Weak' };
  if (score === 2) return { width: '40%', color: '#f97316', label: 'Fair' };
  if (score === 3) return { width: '65%', color: '#eab308', label: 'Good' };
  return { width: '100%', color: '#22c55e', label: 'Strong' };
};

const TenantResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const strength = getStrength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/tenant-portal/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong. Please try again.'); return; }
      setDone(true);
      setTimeout(() => navigate('/tenant/login'), 3000);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="trp-wrap">
        <style>{CSS}</style>
        <div className="trp-card">
          <div className="trp-icon-wrap">⚠️</div>
          <h1 className="trp-title">Invalid link</h1>
          <div className="trp-invalid">
            This reset link is missing or invalid.<br />
            Please request a new one.
          </div>
          <p className="trp-hint">
            <Link to="/tenant/forgot-password">Request a new reset link →</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="trp-wrap">
      <style>{CSS}</style>

      <div className="trp-card">
        <div className="trp-icon-wrap">🔐</div>
        <h1 className="trp-title">Set new password</h1>
        <p className="trp-subtitle">
          Choose a new secure password for your Tenant Portal.
        </p>

        {done ? (
          <div className="trp-success">
            ✅ <strong>Password updated!</strong><br />
            Redirecting you to sign in…
          </div>
        ) : (
          <>
            {error && (
              <div className="trp-error">
                <span>⚠️</span> {error}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 18 }}>
                <label className="trp-label">New password</label>
                <input
                  className="trp-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
                {password && (
                  <div className="trp-strength">
                    <div className="trp-strength-bar" style={{ width: strength.width, background: strength.color }} />
                  </div>
                )}
                {password && (
                  <div style={{ fontSize: 12, color: strength.color, fontWeight: 700, marginTop: 4 }}>
                    {strength.label}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 26 }}>
                <label className="trp-label">Confirm password</label>
                <input
                  className="trp-input"
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" className="trp-btn" disabled={loading}>
                {loading ? 'Saving…' : 'Set New Password →'}
              </button>
            </form>
          </>
        )}

        <p className="trp-hint">
          <Link to="/tenant/login">← Back to sign in</Link>
        </p>
      </div>

      <p className="trp-footer">
        Are you a landlord?{' '}
        <Link to="/login">Sign in here</Link>
      </p>
    </div>
  );
};

export default TenantResetPassword;
